/**
 * Listing-plans service.
 *
 * API-first: served from the backend via GET /plans (table `listing_plans` +
 * companion tables). The bundled mock data is used ONLY when no API URL is
 * configured (preview mode); with a live API, failures propagate so the page
 * can show a real error + retry state.
 */
import { apiGet, isApiConfigured } from "./http";
import { plans as demoPlans, planComparison as demoComparison, planFaqs as demoFaqs } from "@/mocks/plans";
import type { PlansBundle } from "@/types/plans";

const demoBundle = (): PlansBundle => ({
  plans: demoPlans,
  comparison: demoComparison,
  faqs: demoFaqs,
});

/**
 * Collapse duplicate entries by a key, keeping the first occurrence.
 *
 * The plan content tables have no unique key historically, so a re-run of the
 * seed could store the same highlight / comparison row / FAQ twice. De-duping
 * here guarantees the pricing page is always clean, regardless of the data.
 */
function dedupeBy<T>(rows: T[], key: (row: T) => string): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const row of rows) {
    const k = key(row).trim().toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(row);
  }
  return out;
}

/** Normalise an API bundle so no feature is ever shown twice. */
function normalizeBundle(bundle: PlansBundle): PlansBundle {
  return {
    plans: (bundle.plans ?? []).map((plan) => ({
      ...plan,
      highlights: dedupeBy(plan.highlights ?? [], (h) => h),
    })),
    comparison: dedupeBy(bundle.comparison ?? [], (row) => row.feature),
    faqs: dedupeBy(bundle.faqs ?? [], (faq) => faq.question),
  };
}

export async function getPlans(): Promise<PlansBundle> {
  if (!isApiConfigured()) return demoBundle();
  return normalizeBundle(await apiGet<PlansBundle>("/plans"));
}